import { useEffect, useState } from 'react';
import { useMapContext } from './MapProvider';

/**
 * Subscribes to the service area hover store and returns whether
 * the given placeId is currently hovered. Only re-renders when
 * the boolean result changes for this specific placeId.
 */
export const useServiceAreaHover = (placeId: string | undefined): boolean => {
    const { serviceAreaHoverStore } = useMapContext();
    const [isHovered, setIsHovered] = useState(
        () =>
            placeId !== undefined &&
            serviceAreaHoverStore.current.placeId === placeId
    );

    useEffect(() => {
        if (!placeId) return;
        const listener = () => {
            const hovered = serviceAreaHoverStore.current.placeId === placeId;
            setIsHovered((prev) => (prev === hovered ? prev : hovered));
        };
        serviceAreaHoverStore.current.listeners.add(listener);
        // Sync in case hover changed before subscription
        listener();
        return () => {
            serviceAreaHoverStore.current.listeners.delete(listener);
        };
    }, [placeId]);

    return isHovered;
};

/**
 * Like useServiceAreaHover but for multiple placeIds (cluster markers).
 * Returns true if ANY of the given placeIds is currently hovered.
 */
export const useServiceAreaHoverMulti = (placeIds: string[]): boolean => {
    const { serviceAreaHoverStore } = useMapContext();
    const [isHovered, setIsHovered] = useState(() => {
        const current = serviceAreaHoverStore?.current?.placeId;
        return current !== undefined && placeIds.includes(current);
    });

    useEffect(() => {
        const store = serviceAreaHoverStore?.current;
        if (placeIds.length === 0 || !store) return;
        const listener = () => {
            const current = store.placeId;
            const hovered = current !== undefined && placeIds.includes(current);
            setIsHovered(hovered);
        };
        store.listeners.add(listener);
        listener();
        return () => {
            store.listeners.delete(listener);
        };
    }, [placeIds.join(',')]);

    return isHovered;
};
